import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react"
import { TableHead } from "@/components/ui/table"
import { cn } from "@/lib/utils"

export type SortDir = "asc" | "desc"
export interface SortState<TCol extends string = string> {
  col: TCol
  dir: SortDir
}

/**
 * Clickable table header that drives a {col, dir} sort state. Used by
 * CyclesList and CycleDetail — extracted into a shared component so both
 * surfaces look and behave identically (cycle/icon/active styling).
 *
 * Caller owns the state. Click flips dir when the same col is active,
 * resets to "asc" when clicking a different col.
 */
export function makeToggle<TCol extends string>(
  setSort: React.Dispatch<React.SetStateAction<SortState<TCol>>>,
) {
  return (col: TCol) =>
    setSort((prev) =>
      prev.col === col
        ? { col, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { col, dir: "asc" },
    )
}

export function SortableHead<TCol extends string>({
  label,
  col,
  sort,
  onClick,
  align,
  className,
}: {
  label: string
  col: TCol
  sort: SortState<TCol>
  onClick: (col: TCol) => void
  align?: "left" | "right" | "center"
  className?: string
}) {
  const active = sort.col === col
  const Icon = active ? (sort.dir === "asc" ? ArrowUp : ArrowDown) : ArrowUpDown
  return (
    <TableHead
      className={cn(
        align === "right" && "text-right",
        align === "center" && "text-center",
        className,
      )}
    >
      <button
        type="button"
        onClick={() => onClick(col)}
        className={cn(
          "inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wide transition-colors",
          active ? "text-foreground" : "text-muted-foreground hover:text-foreground",
          align === "right" && "flex-row-reverse",
        )}
      >
        {label}
        <Icon className={cn("h-3 w-3", active ? "opacity-100" : "opacity-50")} />
      </button>
    </TableHead>
  )
}
